import { PayloadAction } from '@reduxjs/toolkit';
import * as api from 'api/marketplace/revenue/referral';
import {
  referralMonthlyPayoutActions,
  selectReferralMonthlyPayoutAllQueryString,
  selectReferralMonthlyPayoutFilter,
  selectReferralMonthlyPayoutQueryString,
  selectReferralPayoutDetailModal,
  selectReferralSellerStatusModal,
} from 'features/revenue/referral/referralMonthlyPayoutSlice';
import { toastAction } from 'features/toast/toastSlice';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { SellerPayoutStatusParams } from 'types/marketplace/revenue';
import { AxiosError } from 'axios';
import { SagaIterator } from 'redux-saga';

const {
  initialize,
  setSortType,
  loadData,
  loadDataSuccess,
  loadDataFailure,
  loadExcelData,
  loadExcelDataSuccess,
  loadExcelDataFailure,
  search,
  pageChange,
  resetFilter,
  openSellerStatus,
  loadSellerPayoutStatus,
  loadSellerPayoutStatusSuccess,
  loadSellerPayoutStatusFailure,
  changeSellerStatus,
  putSellerStatus,
  openReferrerPayoutDetail,
  openTotalPayoutDetail,
  loadSellerDetail,
  loadSellerDetailSuccess,
  loadSellerDetailFailure,
  confirmPayoutDetail,
  confirmPayoutDetailSuccess,
  confirmPayoutDetailFailure,
  executePayoutDetail,
  executePayoutDetailFailure,
  executePayoutDetailSuccess,
  resetPayoutDetail,
  resetPayoutDetailSuccess,
  resetPayoutDetailFailure,
  loadPaypalData,
  loadPaypalDataSuccess,
  loadPaypalDataFailure,
} = referralMonthlyPayoutActions;

function* updateDataSaga(): SagaIterator {
  yield put(loadData());
}

function* loadDataSaga(): SagaIterator {
  const query: string = yield select(selectReferralMonthlyPayoutQueryString);
  try {
    const { data } = yield call(api.getSellerMonthlyPayout, query);
    yield put(loadDataSuccess(data));
  } catch (error) {
    yield put(toastAction.errorResponse(error as AxiosError));
    yield put(loadDataFailure());
  }
}

function* loadExcelDataSaga(): SagaIterator {
  const query: string = yield select(selectReferralMonthlyPayoutAllQueryString);
  try {
    const { data } = yield call(api.getSellerMonthlyPayout, query);
    yield put(loadExcelDataSuccess(data));
  } catch (error) {
    yield put(toastAction.errorResponse(error as AxiosError));
    yield put(loadExcelDataFailure());
  }
}

function* openSellerStatusSaga(): SagaIterator {
  yield put(loadSellerPayoutStatus());
}

function* loadSellerPayoutStatusSaga(): SagaIterator {
  const { userId } = yield select(selectReferralSellerStatusModal);
  try {
    const { data } = yield call(api.getReferralPayoutStatusInfo, userId);
    yield put(loadSellerPayoutStatusSuccess(data));
  } catch (error) {
    yield put(toastAction.errorResponse(error as AxiosError));
    yield put(loadSellerPayoutStatusFailure());
  }
}

function* changeSellerPayoutStatusSaga({
  payload: { comment, status },
}: PayloadAction<SellerPayoutStatusParams>): SagaIterator {
  const { userId } = yield select(selectReferralSellerStatusModal);
  if (!comment) {
    yield put(toastAction.toastMessage({ content: 'Please write a comment.' }));
    return;
  }
  yield put(putSellerStatus());
  try {
    const { data } = yield call(api.editReferralPayoutStatus, { userId, status, comment });
    yield put(loadSellerPayoutStatusSuccess(data));
    yield put(loadData());
  } catch (error) {
    yield put(toastAction.errorResponse(error as AxiosError));
    yield put(loadSellerPayoutStatusFailure());
  }
}

function* openReferrerPayoutDetailSaga(): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);
  const { userId, month } = yield select(selectReferralPayoutDetailModal);
  if (!userId) {
    yield put(toastAction.toastMessage({ content: 'no user ID' }));
    return;
  }

  yield put(loadSellerDetail());
  const { data, error } = yield call(api.getSellerPayoutDetail, { userId, year, month });
  if (error) {
    yield put(toastAction.errorResponse(error));
    yield put(loadSellerDetailFailure());
    return;
  }
  yield put(loadSellerDetailSuccess(data));
}

function* openTotalPayoutDetailSaga({ payload: month }: PayloadAction<number>): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);

  yield put(loadSellerDetail());
  const { data, error } = yield call(api.getTotalPayoutDetail, { month, year });
  if (error) {
    yield put(toastAction.errorResponse(error));
    yield put(loadSellerDetailFailure());
    return;
  }
  yield put(loadSellerDetailSuccess(data));
}

function* confirmPayoutDetailSaga(): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);
  const { month } = yield select(selectReferralPayoutDetailModal);

  yield put(loadSellerDetail());
  const { error } = yield call(api.editTotalPayoutConfirm, { year, month });
  if (error) {
    yield put(toastAction.errorResponse(error));
    yield put(confirmPayoutDetailFailure());
    return;
  }
  yield put(confirmPayoutDetailSuccess());
  yield put(openTotalPayoutDetail(month));
}

function* executePayoutDetailSaga(): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);
  const { month } = yield select(selectReferralPayoutDetailModal);

  yield put(loadSellerDetail());
  const { error } = yield call(api.editTotalPayoutExecution, { year, month });
  if (error) {
    yield put(toastAction.errorResponse(error));
    yield put(executePayoutDetailFailure());
    return;
  }
  yield put(executePayoutDetailSuccess());
  yield put(openTotalPayoutDetail(month));
}

function* resetPayoutDetailSaga(): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);
  const { month } = yield select(selectReferralPayoutDetailModal);

  // yield put(loadSellerDetail());
  const { error } = yield call(api.resetPayoutConfirm, { year, month });
  if (error) {
    yield put(toastAction.errorResponse(error));
    yield put(resetPayoutDetailFailure());
    return;
  }
  yield put(resetPayoutDetailSuccess());
  yield put(openTotalPayoutDetail(month));
}

function* loadPaypalDataSaga(): SagaIterator {
  const { year } = yield select(selectReferralMonthlyPayoutFilter);
  const { month } = yield select(selectReferralPayoutDetailModal);
  try {
    const { data } = yield call(api.getPaypalPayoutCSV, { year: year, month: month });
    yield put(loadPaypalDataSuccess(data));
  } catch (error) {
    yield put(toastAction.errorResponse(error as AxiosError));
    yield put(loadPaypalDataFailure());
  }
}

export default function* referralMonthlyPayoutSaga(): SagaIterator {
  yield all([
    takeLatest(loadData, loadDataSaga),
    takeLatest(loadExcelData, loadExcelDataSaga),
    takeLatest(initialize, updateDataSaga),
    takeLatest(search, updateDataSaga),
    takeLatest(pageChange, updateDataSaga),
    takeLatest(resetFilter, updateDataSaga),
    takeLatest(setSortType, updateDataSaga),
    takeLatest(openSellerStatus, openSellerStatusSaga),
    takeLatest(loadSellerPayoutStatus, loadSellerPayoutStatusSaga),
    takeLatest(changeSellerStatus, changeSellerPayoutStatusSaga),
    takeLatest(openTotalPayoutDetail, openTotalPayoutDetailSaga),
    takeLatest(openReferrerPayoutDetail, openReferrerPayoutDetailSaga),
    takeLatest(confirmPayoutDetail, confirmPayoutDetailSaga),
    takeLatest(executePayoutDetail, executePayoutDetailSaga),
    takeLatest(loadPaypalData, loadPaypalDataSaga),
    takeLatest(resetPayoutDetail, resetPayoutDetailSaga),
  ]);
}
